import { ref, reactive, computed, watch } from 'vue'
import Memory from '@/lib/memory'
import { normalizeSamples, savePolarLiveBleCapture, savePolarMemoryCapture } from '@/lib/sqlite'
import { SENSOR_SESSION_LIST_STATUS } from './session-list-status'
import { liveSessionSeconds, pausedMilliseconds } from '@/lib/sessions/live-duration'
import { stopRecordingIntent } from '@/lib/helpers/recording-intents'
import { sendPolarCaptures } from '@/lib/helpers/polar'
import { signedInUserId } from '@/lib/helpers/send-queue'

export default () => {
    // ************************************************
    // ****************  properties ****************
    // ***********************************************
    const driver = ref('')
    const language = ref('')

    const initialSensorProperties = {
        uuid: '',
        deviceId: '',
        name: '',
        canEdit: false,
        alias: '',
        horse_id: '',
        rider_id: '',
        horse: null,
        rider: null,
        firmware: '',
        firmwareNRF: '',
        cumulativeTime: '',
        memory: '',
        turnOffTime: '',
        gpsDate: '',
        // gpsFix == 0 => 'No fix'
        // gpsFix == 1 => 'Dead Reckoning Only'
        // gpsFix == 2 => '2D Fix'
        // gpsFix == 3 => '3D Fix'
        // gpsFix == 4 => 'Combined GPS and Dead Reckoning'
        // gpsFix == 5 => 'Time Fix Only'
        // gpsFixStr => 'Reserved'
        gpsFix: '',
        // gpsFlags & 0b00000001 => 'GPS fix OK'
        // gpsFlags & 0b00000010 => 'Differential solution (DGPS used)'
        // gpsFlags & 0b00000100 => 'Week number set (Valid GPS WN)'
        // gpsFlags & 0b00001000 => 'Time of week set (Valid GPS iTOW)'
        gpsFlags: '',
        gpsSatellites: '',
        gpsAccuracy: '', // (cm)
        gpsDistance: '', // (m)
        gpsSpeed: '', // (cm/s)  x3600 (60x60) to hours // :100:1000
        // https://docs.google.com/spreadsheets/d/108dckBNfHDLugHRFsgxGZhFY4ov4weqGdiVBcqHOd_8/edit#gid=820861251
        fusionStatus: '',
        // stmMode == 0xe3 => 'Navigation mode (is not writing of flash)'
        // stmMode == 0xe4 => 'Navigation mode (is writing of flash)'
        // stmMode == 0xb7 => 'Memory mode'
        // stmMode == 0x25 => 'Calibration mode'
        stmMode: '',
        battery: '',
        filesCount: '',
        assStatus: '',
        ausStatus: '',
        amsMode: '',
        needs_update: '',
        firmware_download_url: '',
        latest_version: '',
        checksum: '',
        // Optional nRF/Nordic firmware descriptors, populated by the backend when
        // a separate BLE firmware is available (see use-firmware-update.js contract).
        needs_update_nordic: '',
        firmware_nordic_download_url: '',
        latest_version_nordic: '',
    }
    const properties = reactive({ ...initialSensorProperties })
    const nearbyDevices = reactive([])
    const files = reactive([])
    const devices = reactive([])
    const fileSizeLimit = 30000 // +- 5s

    /**
     * Check whether a sensor is connected
     */
    const isBusy = ref(false)
    const sessionListStatus = ref(SENSOR_SESSION_LIST_STATUS.IDLE)
    const sessionListFetchedAt = ref(null)
    /**
     * Check whether the channel communication is enable
     */
    const isEnabled = ref(false)
    /**
     * Check whether the Web API is available
     */
    const hasWebAPIAvailble = ref(false)
    /**
     * Check whether a sensor is connected
     */
    const isConnected = ref(false)

    /**
     * Check whether a there is sensor nearby
     */
    const isNearby = ref(false)

    const signalForce = computed(() => {
        // fusionStatus => 0 gpsFix => 0 => 1
        // fusionStatus => 0 gpsFix => 3 => 1
        // fusionStatus => 2 gpsFix => 3 => 2
        // fusionStatus => 0 gpsFix => 2 => 1
        // fusionStatus => 0 gpsFix => 3 => 1
        // fusionStatus => 2 gpsFix => 3 => 2
        // fusionStatus => 34 gpsFix => 3 => 3
        if ((properties.fusionStatus === 32 || properties.fusionStatus === 34) && properties.gpsFix === 3) {
            return 3 // #3cb44b // MAP OK + DISTANCE OK + SPEED OK
        } else if (properties.fusionStatus === 2 && properties.gpsFix === 3) {
            return 2 //#FC3 // MAP OK + DISTANCE OK + SPEED OK
        } else {
            return 1 //#e6194b // MAP HIDDEN + DISTANCE HIDDEN + SPEED HIDDEN
        }
    })

    const formatSensorName = (sensorProperties) => {
        if (sensorProperties?.alias) {
            const nameCleared = sensorProperties?.name?.replace('AlogoMove', '').trim()
            return `${sensorProperties?.alias} - ${nameCleared}`
        }
        if (sensorProperties?.name) {
            return sensorProperties?.name
        }
        return sensorProperties?.serial_id
    }
    const nameFormatted = computed(() => {
        return formatSensorName(properties)
    })

    const isPaused = computed(() => properties.assStatus === 2)
    const isRecording = computed(() => properties.assStatus === 1)
    const isStopped = computed(() => properties.assStatus === 0)

    const isMemoryMode = computed(() => properties.amsMode === 6)

    const hasGpsSignal = computed(() => signalForce.value === 3 || signalForce.value === 2)
    const hasBlockingSensorIssue = ref(false)
    const blockingSensorIssueDismissed = ref(false)

    const setBlockingSensorIssue = (isActive) => {
        const normalizedIsActive = !!isActive

        if (!normalizedIsActive) {
            hasBlockingSensorIssue.value = false
            return
        }

        hasBlockingSensorIssue.value = true
    }

    const dismissBlockingSensorIssue = () => {
        if (!hasBlockingSensorIssue.value) return
        blockingSensorIssueDismissed.value = true
    }

    const showBlockingSensorIssue = () => {
        if (!hasBlockingSensorIssue.value) return
        blockingSensorIssueDismissed.value = false
    }

    const clearBlockingSensorIssue = () => {
        hasBlockingSensorIssue.value = false
        blockingSensorIssueDismissed.value = false
    }

    const createInitialSensorLiveSession = () => ({
        discipline: undefined,
        horseId: undefined,
        riderId: undefined,
        intentId: null,
        goal: null,
        goal_value: null,
        /**
         * Stores the time when the live
         * session started.
         */
        startedAt: undefined,

        /**
         * Stores when the live session
         * has ended at.
         */
        endedAt: undefined,

        /**
         * Stores if the live session is
         * currently paused.
         */
        paused: false,

        /**
         * The protocol step being ridden, when the session follows one:
         * { gait, position, remaining } — written by the program screen each
         * tick, read by the Live Activity so the lock screen shows the same
         * gait and countdown as the phone. null outside a protocol.
         */
        protocol: null,

        /**
         * Whether the live screen guides the horse's program of the day. Set
         * from the "Follow the program" switch of the Record page; false makes
         * the recording a free session (no guidance, no step cue).
         */
        followProgram: true,

        /**
         * What the rider tapped on the program screen (record/RecordingInterface):
         * "Next gait" (seconds the program clock moved forward) and "+1 min"
         * (extra seconds per gait index). Held here, not by the screen, so
         * leaving the live page and coming back finds the program where it was.
         */
        programOffset: 0,
        gaitExtensions: {},

        /**
         * Stores the pauses that has been
         * performed during the live session.
         *
         * Format: { from: null, to: null }
         */
        pauses: [],

        /**
         * Stores the current time. This is updated
         * every second. This must exist due the fact
         * that the computer properties need to know when
         * the data changes, and now it's always in motion.
         *
         * For reference:
         * https://github.com/vuejs/Discussion/issues/214#issuecomment-116122788
         */
        now: { time: undefined, handler: undefined },

        /**
         * Heartrate values stream
         *
         * Format: [{'60':5}...]
         */
        heartrateStream: [],
        /**
         * ECG values stream
         *
         * Format: [{60:[5,10,2,...]}...]
         */
        ecgStream: [],
        usePolar: false,
        sensorSerialId: null,
        sensorName: null,
        polarDeviceId: null,
        polarDeviceName: null,
        polarExpectedExerciseTs: null,
        polarMemoryExerciseTs: null,
        polarDeletedFromPolarAt: null,
        polarBleBackupStream: [],
        recordingIntentStoppedAt: null,
    })
    const liveSession = reactive(createInitialSensorLiveSession())
    const liveSessionFinalizing = ref(false)
    const liveSessionFinalizationPauseCount = ref(0)

    // return live distance in meters
    const liveDistance = computed(() => {
        if (hasGpsSignal.value && properties.gpsDistance) {
            return properties.gpsDistance.toFixed(2)
        }
        return 0
    })

    // return live speed in m/min
    const liveSpeed = computed(() => {
        if (hasGpsSignal.value && properties.gpsSpeed) {
            return (properties.gpsSpeed * 0.6).toFixed(2)
        }
        return 0
    })

    /**
     * Determines the duration of the current live session in seconds,
     * frozen while paused (lib/sessions/live-duration.js).
     */
    const liveDuration = computed(() => liveSessionSeconds(liveSession))

    const liveSessionGoalPercentage = computed(() => {
        try {
            if (!liveSession.startedAt) return 0
            const goal = liveSession.goal
            let percent = 0
            if (goal === null || goal === undefined || goal === 'open_goal') return percent
            switch (goal) {
                case 'distance':
                    if (hasGpsSignal.value) {
                        percent = (liveDistance.value * 100) / liveSession.goal_value
                    } else {
                        percent = 0
                    }
                    break
                case 'duration':
                    percent = (liveDuration.value * 100) / liveSession.goal_value

                    break
                default:
                    break
            }
            return Math.floor(percent)
        } catch (err) {
            console.log('liveSessionGoalPercentage', err)
            return 0
        }
    })

    const liveSessionGoalCompleted = computed(() => {
        try {
            if (!liveSession.startedAt) return false
            const goal = liveSession.goal
            if (goal === null || goal === undefined || goal === 'open_goal') return false
            return liveSessionGoalPercentage.value >= 100
        } catch (err) {
            console.log('liveSessionGoalCompleted', err)
            return false
        }
    })

    const pausesInSec = () => pausedMilliseconds(liveSession)

    // ************************************************
    // **************** methods ****************
    // ***********************************************
    const resetLiveSession = () => {
        Object.assign(liveSession, createInitialSensorLiveSession())
    }
    const resetSensorProperties = () => {
        Object.assign(properties, initialSensorProperties)
    }
    const resetSensorFiles = () => {
        files.splice(0) //to not break the reactivity
        properties.filesCount = 0
    }
    const resetSessionListStatus = () => {
        sessionListStatus.value = SENSOR_SESSION_LIST_STATUS.IDLE
        sessionListFetchedAt.value = null
    }
    const markSessionListLoading = () => {
        sessionListStatus.value = SENSOR_SESSION_LIST_STATUS.LOADING
    }
    const markSessionListLoaded = () => {
        sessionListStatus.value = SENSOR_SESSION_LIST_STATUS.LOADED
        sessionListFetchedAt.value = Date.now()
    }
    const markSessionListFailed = () => {
        sessionListStatus.value = SENSOR_SESSION_LIST_STATUS.FAILED
    }
    const markSessionListBlocked = () => {
        sessionListStatus.value = SENSOR_SESSION_LIST_STATUS.BLOCKED
    }
    const isSessionListLoading = () => sessionListStatus.value === SENSOR_SESSION_LIST_STATUS.LOADING
    const resetSensor = () => {
        resetSensorProperties()
        resetSensorFiles()
        resetSessionListStatus()
        clearBlockingSensorIssue()
    }

    watch(
        () => properties.assStatus,
        (assStatus) => {
            if (assStatus === 1 || assStatus === 2) {
                resetSessionListStatus()
            }
        }
    )

    const clearPersistedSensorEntries = async () => {
        const sensorSerialId = properties.uuid
        if (!sensorSerialId) return 0

        const keysList = (await Memory.keys()) || []
        let removedKeys = 0

        for (const key of keysList) {
            if (!/^D[0-9]+/.test(key)) continue

            try {
                const fileProperties = await Memory.get(key)
                if (!fileProperties?.uuid || fileProperties.uuid === sensorSerialId) {
                    await Memory.remove(key)
                    removedKeys++
                }
            } catch (error) {
                console.warn('[Sensor] Failed to remove persisted sensor file entry', key, error)
            }
        }

        return removedKeys
    }

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

    const startLiveSessionHandler = () => {
        // liveSession.now.handler = setInterval(() => {
        //     liveSession.now.time = Date.now()
        // }, 100)

        //every seconds
        liveSession.now.handler = setInterval(() => {
            liveSession.now.time = Date.now()
        }, 1000)
    }

    const isLiveSessionOwnedBy = (sensorSerialId = null) => {
        return !!liveSession.sensorSerialId && !!sensorSerialId && liveSession.sensorSerialId === sensorSerialId
    }

    const startLiveSession = (usePolar = false, polarMeta = {}, sensorMeta = {}) => {
        liveSession.usePolar = usePolar
        //fix reset heartrate stream at start
        liveSession.heartrateStream = []
        liveSession.polarBleBackupStream = []
        liveSession.polarExpectedExerciseTs = usePolar ? polarMeta?.expectedExerciseTs || null : null
        liveSession.polarMemoryExerciseTs = null
        liveSession.polarDeletedFromPolarAt = null
        liveSession.sensorSerialId = sensorMeta?.sensorSerialId || properties.uuid || null
        liveSession.sensorName = sensorMeta?.sensorName || properties.name || null
        liveSession.polarDeviceId = usePolar ? polarMeta?.deviceId || null : null
        liveSession.polarDeviceName = usePolar ? polarMeta?.deviceName || null : null
        liveSession.recordingIntentStoppedAt = null
        liveSession.startedAt = Date.now()
        liveSession.now.time = Date.now()
        startLiveSessionHandler()
    }

    const stopLiveSession = async (endedAt = Date.now()) => {
        if (!liveSession.startedAt) {
            return false
        }

        if (!liveSession.endedAt) {
            liveSession.endedAt = endedAt
        }
        // Clear the interval for the now update.
        clearInterval(liveSession.now.handler)
        // Reset the now timestamps.
        liveSession.now.time = undefined
        liveSession.now.handler = undefined

        if (liveSession.intentId && !liveSession.recordingIntentStoppedAt) {
            try {
                await stopRecordingIntent(liveSession.intentId, liveSession.endedAt)
                liveSession.recordingIntentStoppedAt = liveSession.endedAt
            } catch (error) {
                console.warn('[Sensor] Failed to mark recording intent as stopped', error)
            }
        }

        return true
    }

    const buildLiveSessionSnapshot = () => {
        if (!liveSession.startedAt) return null

        return {
            intentId: liveSession.intentId ?? null,
            startedAt: liveSession.startedAt ?? null,
            endedAt: liveSession.endedAt ?? null,
            usePolar: !!liveSession.usePolar,
            sensorSerialId: liveSession.sensorSerialId || null,
            sensorName: liveSession.sensorName || null,
            polarDeviceId: liveSession.polarDeviceId || null,
            polarDeviceName: liveSession.polarDeviceName || null,
            polarExpectedExerciseTs: liveSession.polarExpectedExerciseTs || null,
            polarMemoryExerciseTs: liveSession.polarMemoryExerciseTs || null,
            polarDeletedFromPolarAt: liveSession.polarDeletedFromPolarAt || null,
            heartrateStream: normalizeSamples(liveSession.heartrateStream),
            polarBleBackupStream: normalizeSamples(liveSession.polarBleBackupStream),
        }
    }

    const pauseLiveSessionFinalization = () => {
        liveSessionFinalizationPauseCount.value += 1
        let resumed = false

        return () => {
            if (resumed) return
            resumed = true
            liveSessionFinalizationPauseCount.value = Math.max(0, liveSessionFinalizationPauseCount.value - 1)
        }
    }

    const finalizeLiveSessionAfterSensorStop = async ({ refreshFiles } = {}) => {
        if (liveSessionFinalizing.value || liveSessionFinalizationPauseCount.value > 0 || !liveSession.startedAt) {
            return null
        }

        liveSessionFinalizing.value = true
        try {
            await stopLiveSession()
            const snapshot = buildLiveSessionSnapshot()

            if (typeof refreshFiles === 'function') {
                await refreshFiles()
            }

            await saveLiveSessionToLatestFile()
            return snapshot
        } finally {
            liveSessionFinalizing.value = false
        }
    }

    // The heart rate of the ride, kept on the phone and sent to the server with
    // its launch's uuid (P7): the live Bluetooth stream, and the Polar memory
    // when it was imported at the stop. The live stream of a belt other than a
    // Polar is kept the same way, without its device: it no longer goes with
    // the MOVE PRO file.
    const persistPolarCapturesForLiveSession = async () => {
        if (!liveSession.startedAt || !liveSession.endedAt) {
            return
        }

        const deviceId = liveSession.polarDeviceId || null
        const deviceName = liveSession.polarDeviceName || null
        const recordingIntentId = liveSession.intentId ?? null
        const userId = signedInUserId()

        const liveBleSamples = normalizeSamples(
            liveSession.polarBleBackupStream?.length
                ? liveSession.polarBleBackupStream
                : !liveSession.polarMemoryExerciseTs
                  ? liveSession.heartrateStream
                  : []
        )
        const memorySamples = liveSession.polarMemoryExerciseTs ? normalizeSamples(liveSession.heartrateStream) : []

        const operations = []

        if (liveBleSamples.length) {
            operations.push(
                savePolarLiveBleCapture({
                    deviceId,
                    deviceName,
                    startedAt: liveSession.startedAt,
                    endedAt: liveSession.endedAt,
                    recordingIntentId,
                    userId,
                    samples: liveBleSamples,
                })
            )
        }

        if (memorySamples.length && liveSession.polarMemoryExerciseTs) {
            operations.push(
                savePolarMemoryCapture({
                    deviceId,
                    deviceName,
                    polarExerciseTs: liveSession.polarMemoryExerciseTs,
                    startedAt: liveSession.startedAt,
                    endedAt: liveSession.endedAt,
                    recordingIntentId,
                    userId,
                    samples: memorySamples,
                    deletedFromPolarAt: liveSession.polarDeletedFromPolarAt || undefined,
                })
            )
        }

        if (!operations.length) {
            return
        }

        const results = await Promise.allSettled(operations)
        for (const result of results) {
            if (result.status === 'rejected') {
                console.warn('[Sensor] Failed to persist Polar capture', result.reason)
            }
        }
        void sendPolarCaptures()
    }

    const saveLiveSessionToLatestFile = async () => {
        await persistPolarCapturesForLiveSession()
        resetLiveSession()
    }

    // ************************************************
    // **************** to override methods ****************
    // ***********************************************
    /**
     * Open a connection to a sensor
     */
    const connect = async () => {
        throw new Error('Not implemented connect')
    }
    /**
     * Close a connection to a sensor
     */
    const disconnect = async (forgetDevice = false) => {
        throw new Error('Not implemented disconnect')
    }
    /**
     * Read the uuid
     */
    const getUUID = async () => {
        throw new Error('Not implemented getUUID')
    }
    /**
     * Set to power on mode
     */
    const powerOn = async () => {
        throw new Error('Not implemented powerOn')
    }
    /**
     * Shutdown the sensor
     */
    const shutdown = async () => {
        throw new Error('Not implemented shutdown')
    }
    /**
     * Set to OTA mode / DFU
     */
    const setOTAMode = async () => {
        throw new Error('Not implemented setOTAMode')
    }
    /**
     * Set to init mode
     */
    const setInitMode = async () => {
        throw new Error('Not implemented setInitMode')
    }
    /**
     * Set to init mode
     */
    const setADVMode = async () => {
        throw new Error('Not implemented setADVMode')
    }
    /**
     * Set to navigation mode
     */
    const setNavigationMode = async () => {
        throw new Error('Not implemented setNavigationMode')
    }
    /**
     * Set to memory mode
     */
    const setMemoryMode = async () => {
        throw new Error('Not implemented setMemoryMode')
    }
    /**
     * Set to calibration mode
     */
    const setCalibrationMode = async () => {
        throw new Error('Not implemented setCalibrationMode')
    }
    /**
     * Set to lost mode
     */
    const setLostMode = async () => {
        throw new Error('Not implemented setLostMode')
    }
    /**
     * Reset sensor settings
     */
    const resetSettings = async () => {
        throw new Error('Not implemented resetSettings')
    }
    /**
     * Set to STMBL mode
     */
    const setSTMBLMode = async () => {
        throw new Error('Not implemented setSTMBLMode')
    }
    /**
     * Set to STMNBL mode
     */
    const setSTMNBLMode = async () => {
        throw new Error('Not implemented setSTMNBLMode')
    }
    /**
     * Set to updateFirmware
     */
    const updateFirmware = async () => {
        throw new Error('Not implemented updateFirmware')
    }
    /**
     * Set to updateFirmwareNRF
     */
    const updateFirmwareNRF = async () => {
        throw new Error('Not implemented updateFirmwareNRF')
    }
    /**
     * Flash an STM firmware image (downloaded from the backend) over the Alogo
     * bootloader. onProgress is called with the completion percentage (0-100).
     */
    const updateFirmwareSTM = async () => {
        throw new Error('Not implemented updateFirmwareSTM')
    }
    /**
     * Get the current mode
     */
    const getMode = async () => {
        throw new Error('Not implemented getMode')
    }
    /**
     * Remove the sensor memory
     */
    const eraseMemory = async () => {
        throw new Error('Not implemented eraseMemory')
    }
    /**
     * IDLE
     */
    const idle = async () => {
        throw new Error('Not implemented idle')
    }
    /**
     * RECORD
     */
    const record = async () => {
        throw new Error('Not implemented record')
    }
    /**
     * PAUSE
     */
    const pause = async () => {
        throw new Error('Not implemented pause')
    }
    /**
     * RESUME
     */
    const resume = async () => {
        throw new Error('Not implemented resume')
    }
    /**
     * STOP
     */
    const stop = async () => {
        throw new Error('Not implemented stop')
    }
    /**
     * GET_LIST
     */
    const getSessionsList = async () => {
        throw new Error('Not implemented getSessionsList')
    }
    /**
     * GET_LIGHT_SESSION
     */
    const syncSelectedSessions = async () => {
        throw new Error('Not implemented syncSelectedSessions')
    }
    /**
     * ASK PERMISSIONS
     */
    const askPermissions = async () => {
        throw new Error('Not implemented askPermissions')
    }

    return {
        driver,
        language,
        nearbyDevices,
        liveSession,
        properties,
        files,
        fileSizeLimit,
        resetLiveSession,
        resetSensorProperties,
        resetSensorFiles,
        resetSensor,
        clearPersistedSensorEntries,
        signalForce,
        sleep,
        startLiveSession,
        stopLiveSession,
        buildLiveSessionSnapshot,
        pauseLiveSessionFinalization,
        finalizeLiveSessionAfterSensorStop,
        isLiveSessionOwnedBy,
        saveLiveSessionToLatestFile,
        isBusy,
        sessionListStatus,
        sessionListFetchedAt,
        resetSessionListStatus,
        markSessionListLoading,
        markSessionListLoaded,
        markSessionListFailed,
        markSessionListBlocked,
        isSessionListLoading,
        isEnabled,
        hasWebAPIAvailble,
        isConnected,
        isNearby,
        formatSensorName,
        nameFormatted,
        isPaused,
        isRecording,
        isStopped,
        isMemoryMode,
        hasGpsSignal,
        hasBlockingSensorIssue,
        blockingSensorIssueDismissed,
        liveDistance,
        liveSpeed,
        liveDuration,
        liveSessionGoalPercentage,
        liveSessionGoalCompleted,
        pausesInSec,
        setBlockingSensorIssue,
        dismissBlockingSensorIssue,
        showBlockingSensorIssue,
        clearBlockingSensorIssue,
        connect,
        disconnect,
        getUUID,
        powerOn,
        shutdown,
        setOTAMode,
        setInitMode,
        setADVMode,
        setNavigationMode,
        setMemoryMode,
        setCalibrationMode,
        resetSettings,
        setLostMode,
        setSTMBLMode,
        setSTMNBLMode,
        updateFirmware,
        updateFirmwareNRF,
        updateFirmwareSTM,
        getMode,
        eraseMemory,
        idle,
        record,
        pause,
        resume,
        getSessionsList,
        syncSelectedSessions,
        askPermissions,
    }
}
